import { useEffect, useState, useMemo } from 'react'
import { Plus, Pencil, Trash2, Loader2, Filter } from 'lucide-react'
import { useAdmin } from '../../hooks/use-admin'
import { ConfigFormDialog } from './ConfigFormDialog'
import { useI18n } from '../../i18n'
import type { AdminConfig, AdminConfigCreateRequest, AdminConfigUpdateRequest } from '../../types'

export function ConfigSection() {
  const { configs, loading, fetchConfigs, createConfig, updateConfig, deleteConfig } = useAdmin()
  const { t } = useI18n()

  const [groupFilter, setGroupFilter] = useState<string>('')
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editing, setEditing] = useState<AdminConfig | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<AdminConfig | null>(null)

  useEffect(() => {
    fetchConfigs(groupFilter || undefined)
  }, [fetchConfigs, groupFilter])

  const groups = useMemo(() => {
    const set = new Set(configs.map((c) => c.configGroup).filter(Boolean))
    return Array.from(set).sort()
  }, [configs])

  const handleCreate = async (data: AdminConfigCreateRequest): Promise<boolean> => {
    const ok = await createConfig(data)
    if (ok) fetchConfigs(groupFilter || undefined)
    return ok
  }

  const handleUpdate = async (data: AdminConfigCreateRequest): Promise<boolean> => {
    if (!editing) return false
    const req: AdminConfigUpdateRequest = {
      id: editing.id,
      configValue: data.configValue,
      configName: data.configName,
      description: data.description,
      sortOrder: data.sortOrder,
    }
    const ok = await updateConfig(req)
    if (ok) fetchConfigs(groupFilter || undefined)
    return ok
  }

  const handleDelete = async () => {
    if (!deleteTarget) return
    const ok = await deleteConfig(deleteTarget.id)
    if (ok) fetchConfigs(groupFilter || undefined)
    setDeleteTarget(null)
  }

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Filter className="h-3.5 w-3.5 text-gray-400" />
          <select
            value={groupFilter}
            onChange={(e) => setGroupFilter(e.target.value)}
            className="h-8 text-[13px] px-2.5 rounded-lg bg-white border border-black/[0.06] focus:border-orange-200 focus:outline-none"
          >
            <option value="">{t('全部分组', 'All Groups')}</option>
            {groups.map((g) => (
              <option key={g} value={g}>{g}</option>
            ))}
          </select>
        </div>
        <button
          onClick={() => { setEditing(null); setDialogOpen(true) }}
          className="flex items-center gap-1.5 px-3 py-1.5 text-[13px] rounded-xl bg-orange-500 text-white hover:bg-orange-600 transition-all"
        >
          <Plus className="h-3.5 w-3.5" />
          {t('新增', 'Create')}
        </button>
      </div>

      {/* Config list */}
      {loading && configs.length === 0 ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-5 w-5 animate-spin text-orange-500" />
        </div>
      ) : configs.length === 0 ? (
        <div className="text-center py-16 text-sm text-gray-400">
          {t('暂无配置', 'No configs')}
        </div>
      ) : (
        <div className="rounded-xl border border-black/[0.06] overflow-hidden">
          <div className="grid grid-cols-[1fr_1fr_80px_90px_64px] gap-3 px-4 py-2.5 bg-black/[0.02] text-[11px] text-gray-400 font-medium border-b border-black/[0.04]">
            <span>{t('配置', 'Config')}</span>
            <span>{t('配置值', 'Value')}</span>
            <span>{t('类型', 'Type')}</span>
            <span>{t('分组', 'Group')}</span>
            <span className="text-right">{t('操作', 'Actions')}</span>
          </div>
          {configs.map((config) => (
            <div
              key={config.id}
              className="grid grid-cols-[1fr_1fr_80px_90px_64px] gap-3 px-4 py-3 border-b border-black/[0.03] last:border-b-0 hover:bg-black/[0.01] transition-colors group items-center"
            >
              <div className="min-w-0">
                <span className="text-[13px] text-[#1a1a1a] truncate block font-medium">
                  {config.configName || config.configKey}
                </span>
                <span className="text-[11px] text-gray-400 font-mono truncate block">
                  {config.configKey}
                </span>
              </div>
              <span className="text-[13px] font-mono text-gray-600 truncate">
                {config.configValue}
              </span>
              <span className="text-[11px] px-1.5 py-0.5 rounded-md bg-black/[0.04] text-gray-500 font-mono w-fit">
                {config.valueType}
              </span>
              <span className="text-[11px] px-1.5 py-0.5 rounded-md bg-orange-50 text-orange-500 w-fit">
                {config.configGroup}
              </span>
              <div className="flex items-center justify-end gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                <button
                  onClick={() => { setEditing(config); setDialogOpen(true) }}
                  className="p-1.5 rounded-lg text-gray-400 hover:text-[#1a1a1a] hover:bg-black/[0.04] transition-all"
                >
                  <Pencil className="h-3.5 w-3.5" />
                </button>
                <button
                  onClick={() => setDeleteTarget(config)}
                  className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-all"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <ConfigFormDialog
        open={dialogOpen}
        onClose={() => { setDialogOpen(false); setEditing(null) }}
        onSubmit={editing ? handleUpdate : handleCreate}
        editing={editing}
      />

      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={() => setDeleteTarget(null)} />
          <div className="relative bg-white rounded-2xl border border-black/[0.06] shadow-xl w-full max-w-sm mx-4 p-5">
            <h3 className="text-sm font-semibold text-[#1a1a1a] mb-2">
              {t('确认删除', 'Confirm Delete')}
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              {t('确定要删除配置', 'Delete config')} <span className="text-orange-600 font-medium">{deleteTarget.configName || deleteTarget.configKey}</span>？
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setDeleteTarget(null)}
                className="px-4 py-2 text-[13px] rounded-xl bg-black/[0.04] text-gray-400 hover:text-[#1a1a1a] transition-all"
              >
                {t('取消', 'Cancel')}
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 text-[13px] rounded-xl bg-red-500 text-white hover:bg-red-600 transition-all"
              >
                {t('删除', 'Delete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
